import {LoggedInUser, useCallbackLogin, useCallbackToken} from '@api'
import {Language, NavigationPathKey} from '@enums'
import {getUserLanguageAtom} from '@state'

import {setAuthenticationStorage, useViewNavigate} from 'common'
import i18next from 'i18next'
import {useAtomValue} from 'jotai'
import {useTranslation} from 'react-i18next'
import {decodeToken} from 'react-jwt'
import {useLocation} from 'react-router-dom'

type IdTokenPayload = {nonce: string} & Record<string, string | number | string[]>

export function useCallbackModel() {
  const {t} = useTranslation()

  const getUserLanguage = useAtomValue(getUserLanguageAtom)
  const navigate = useViewNavigate()

  const {mutate: callbackTokenMutation} = useCallbackToken()
  const {mutate: callbackLoginMutation} = useCallbackLogin()

  const location = useLocation()

  /**
   * A function to handle the callback redirect
   */
  const onCallback = () => {
    const params = new URLSearchParams(location.search)
    const code = params.get('code') || ''
    const state = params.get('state') || ''

    const expectedState = localStorage.getItem('oidc_state')

    if (!expectedState || state !== expectedState) {
      throw new Error('Invalid state: possible CSRF attack')
    }

    callbackTokenMutation(code, {
      onSuccess: ({id_token}) => {
        const idTokenPayload = decodeToken<IdTokenPayload>(id_token)
        const expectedNonce = localStorage.getItem('oidc_nonce')

        if (!expectedNonce || idTokenPayload?.nonce !== expectedNonce) {
          throw new Error('Invalid nonce: possible token replay attack')
        }

        localStorage.removeItem('oidc_state')
        localStorage.removeItem('oidc_nonce')

        callbackLoginMutation(id_token, {
          onSuccess: ({token}) => {
            const decodedToken: LoggedInUser | null = decodeToken(token)

            if (!decodedToken) {
              throw new Error('INVALID_TOKEN')
            }

            setAuthenticationStorage(
              JSON.stringify({
                token,
                userData: {...decodedToken},
              }),
            )

            i18next.changeLanguage(getUserLanguage(decodedToken.id) || Language.EN)

            navigate(NavigationPathKey.DASHBOARD)
          },
        })
      },
    })
  }

  return {onCallback, t, navigate}
}
